import { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { toggleListItemExpand, useListContext, useRecordContext, useResourceContext } from 'ra-core';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import CreateOrEdit from '@/controller/CreateOrEdit';
import WithSave from '@/controller/WithSave';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';
import { useHasPermit } from '@/authProvider/useHasPermit';
import { MANAGE_DATA_CATALOG } from '@/constants';
import { StateErrors, StateTouched, usePlainStateFormUtils } from '../input/usePlainStateFormUtils';
import { TableSchemaResponseDto } from '@/gen/api/governance';
import { RecPatch } from '@/dataProvider/tools';


const validationSchema =
  Yup.object().shape({
    description: Yup.string().max(512),
  })

const RowExpandTableSemanticEdit: FC = () => {
  const table = useRecordContext<TableSchemaResponseDto&RecPatch>();
  const resource = useResourceContext() as string;
  const dispatch = useDispatch();
  const hasWritePermit = useHasPermit([MANAGE_DATA_CATALOG]);

  const [states, setStates] = useState({
    prevData: table,
    stage: {
      description: table?.description ?? '',
    },
    errors: {} as StatesError,
    touched: {} as StatesTouched,
  })
  const dirty = Object.values(states.touched).some(x => x)

  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, validationSchema)

  // Accessibility: focus the expanded area
  const dialogRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    setTimeout(() => {
      dialogRef.current?.focus();
    });
  }, []);

  const closeExpanded = () => {
    dispatch(toggleListItemExpand(resource, table.id));
  };

  const handlers = useDefaultSavedHandlers('Save Table Semantic Info')

  const { refetch } = useListContext()
  const handleSave = (save: (record: any) => Promise<void>) => async () => {
    if (!hasWritePermit) {
      return;
    }
    const states = await trySubmitValidate()

    await save({ ...states.stage, databaseId: states.prevData.databaseId, tableId: states.prevData.tableId })
    refetch()
    closeExpanded();
  };

  const handleCancel = () => {
    closeExpanded();
  };

  return (
    <Box role="dialog">
      <span tabIndex={-1} ref={dialogRef} className="sr-only">Table Edit Region for {table?.name}</span>
      <Stack direction="row" sx={{ mx: 1, my: 2, '& .MuiFormControl-root': { ml: '42px' } }}>
        <Typography variant="subtitle1" id={`table-desc-label-${table.id}`}>Description</Typography>
        <TextField multiline rows={4} fullWidth aria-labelledby={`table-desc-label-${table.id}`}
          {...fieldProps('description')}
          disabled={!hasWritePermit}
          data-testid="table-description-field"
        />
      </Stack>
      <Box sx={{ 
        mb: -1,
        mx: -2,
        py: '10px',
        px: 5,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        '& button': {
          width: 96,
          mr: 2,
        }, 
      }}>
        {hasWritePermit && (
          <CreateOrEdit resource={resource} basePath="" {...handlers}
            record={table}
            id={table.id}
          >
            <WithSave async
              render={({ save, saving }) => (
                <Button variant="contained" disabled={!dirty || saving}
                  onClick={handleSave(save)}
                  data-testid="save-table-button"
                >
                  Save
                </Button>
              )}
            />
          </CreateOrEdit>
        )}
        <Button
          onClick={handleCancel}
          data-testid="cancel-table-button"
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default RowExpandTableSemanticEdit;

interface UIState {
  description: string;
}
type StatesError = StateErrors<UIState>;
type StatesTouched = StateTouched<UIState>;
